import { FC, useEffect, useState, Suspense } from "react";
import styles from "./AmenitySectionStyles.module.scss";
import Tag from "./Tag";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import Button from "@Components/Button";
import Image from "@Components/Image";
import Modal from "@Components/Modal";
import Portal from "@Components/Portal";
import FullPageLoader from "@Components/FullPageLoader";
import GalleryCarousel from "../../GalleryCarousel";
import getImagePath from "@Utils/getImagePath";
import HorizontalScroll from "@Components/HorizontalScroll";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import ArrowDownIcon from "@Icons/ArrowDownIcon";
//

const AmenitySection: FC<any> = ({
  title,
  titleEn,
  amenityList,
  withDivider,
  showAll,
  isNewVersion = false,
  roomTypeSection = false,
  isDesktop = false,
  modalView = false,
  dataTestId,
  showMore = false,
  amenitiesImages = [],
  isFirstSectionInModal = false,
  isLastSectionInModal = false,
}) => {
  const ITEMS_TO_SHOW = 6;
  const [showFullAmenities, setShowFullAmenities] = useState(showAll);
  const [updatedAmenityList, setUpdatedAmenityList] = useState(
    showAll ? amenityList : amenityList?.slice(0, ITEMS_TO_SHOW),
  );
  const [imageIndex, setImageIndex] = useState(-1);

  useEffect(() => {
    if (showFullAmenities) {
      setUpdatedAmenityList(amenityList);
    } else {
      setUpdatedAmenityList(amenityList?.slice(0, ITEMS_TO_SHOW));
    }
  }, [showFullAmenities]);

  const { t } = useTranslationHook();

  return (
    <>
      <div
        className={formatClassNames(
          styles.container,
          roomTypeSection && styles.roomTypeWrap,
          isDesktop && styles.desktop,
          modalView && styles.modalView,
          modalView && isFirstSectionInModal && styles.modalViewNoTopPadding,
          modalView && isLastSectionInModal && styles.modalViewNoBottomPadding,
        )}
      >
        {(titleEn || title) === "Common Amenities" && amenitiesImages?.length > 0 && (
          <HorizontalScroll
            className={styles.amenitiesImages}
            showControls={isDesktop}
            itemSize={211 * amenitiesImages.length}
          >
            {amenitiesImages.map(({ path }: any, index: number) => (
              <Image
                src={getImagePath(path)}
                height="122px"
                width="211px"
                onClick={() => setImageIndex(index)}
                className={styles.amenitiesImg}
              />
            ))}
          </HorizontalScroll>
        )}
        <h3
          data-testid="property-amenities-section-header"
          className={formatClassNames(
            roomTypeSection && !isDesktop && styles.roomType,
            styles.heading,
            isDesktop && styles.desktop,
          )}
        >
          {(titleEn || title) === "Bills Included"
            ? t("propertypage.amenitiessection.amenitysection.billsincluded")
            : t(title)}
        </h3>

        <div
          className={formatClassNames(
            styles.tagsContainer,
            isDesktop && styles.desktop,
            roomTypeSection && !isDesktop && styles.roomType,
          )}
        >
          {updatedAmenityList.map((amenity: any, idx: number) => {
            const amenityName = amenity.name ?? amenity.type;
            const amenityType = `${amenity.name_en ? amenity.name_en : amenity.name} ${
              amenity.type
            }`;
            return (
              <Tag
                dataTestId={dataTestId}
                key={`${amenityName}-${idx}`}
                icon={amenityType}
                value={amenityName}
                isNewVersion={isNewVersion}
                index={idx}
                roomTypeSection={!!roomTypeSection}
                isDesktop={isDesktop}
              >
                {amenityName}
              </Tag>
            );
          })}
          {showMore && amenityList.length > ITEMS_TO_SHOW && !isDesktop && (
            <Button
              type="button"
              onClick={() => {
                setShowFullAmenities((prev: any) => !prev);
                gtagEventEmitter({
                  action: "room-type-amenities-clicked",
                  category: "room-type",
                  label: "view_more_details",
                });
              }}
              variant="subtle"
              className={formatClassNames(
                styles.toggleTextButton,
                showFullAmenities && styles.less,
                styles.noMargin,
              )}
              RightIcon={ArrowDownIcon}
            >
              {showFullAmenities
                ? t("propertypage.amenitiessection.amenitysection.showless")
                : t("propertypage.amenitiessection.amenitysection.showmore")}
            </Button>
          )}
        </div>
        {showMore && amenityList.length > ITEMS_TO_SHOW && isDesktop && (
          <Button
            type="button"
            onClick={() => {
              setShowFullAmenities((prev: any) => !prev);
              gtagEventEmitter({
                action: "room-type-amenities-clicked",
                category: "room-type",
                label: "view_more_details",
              });
            }}
            variant="subtle"
            className={formatClassNames(styles.toggleTextButton, showFullAmenities && styles.less)}
            RightIcon={ArrowDownIcon}
          >
            {showFullAmenities
              ? t("propertypage.amenitiessection.amenitysection.showless")
              : t("propertypage.amenitiessection.amenitysection.showmore")}
          </Button>
        )}

        {imageIndex !== -1 && (
          <Portal rootId="forPortal">
            <Modal
              hideModal={() => setImageIndex(-1)}
              size="fullScreen"
              animation="bottomToTop"
              hideCloseButton
              zIndex={100000}
              type="galleryCarousel"
              isHistoryPush={!isDesktop}
            >
              <Suspense fallback={<FullPageLoader />}>
                <GalleryCarousel
                  currentCategoryItems={amenitiesImages}
                  initialItemIndex={imageIndex}
                  isDesktop={isDesktop}
                  showViewRoomButton={false}
                  onClose={() => setImageIndex(-1)}
                  showShare={false}
                />
              </Suspense>
            </Modal>
          </Portal>
        )}
      </div>
      {withDivider && (
        <div className={formatClassNames(styles.divider, showAll && styles.showAll)} />
      )}
    </>
  );
};

export default wrapperHOC(AmenitySection, {
  componentName: "AmenitySection-PropertyPage",
  showForChina: true,
});
